import { useRef, useState, useEffect, useCallback } from 'react';
import { CompactProductCard } from './CompactProductCard';
import type { ProductListing } from '../../types/products';

interface RelatedProductsSliderProps {
  products: ProductListing[];
  onAddToCart: (productId: number, formatId: number, quantity: number) => Promise<void>;
}

export function RelatedProductsSlider({ products, onAddToCart }: RelatedProductsSliderProps) {
  const trackRef = useRef<HTMLDivElement>(null);
  const [canScrollLeft, setCanScrollLeft] = useState(false);
  const [canScrollRight, setCanScrollRight] = useState(true);

  const updateArrows = useCallback(() => {
    if (!trackRef.current) return;
    const { scrollLeft, scrollWidth, clientWidth } = trackRef.current;
    setCanScrollLeft(scrollLeft > 10);
    setCanScrollRight(scrollLeft < scrollWidth - clientWidth - 10);
  }, []);

  useEffect(() => {
    const track = trackRef.current;
    if (!track) return;
    updateArrows();

    let ticking = false;
    const onScroll = () => {
      if (ticking) return;
      ticking = true;
      requestAnimationFrame(() => {
        updateArrows();
        ticking = false;
      });
    };

    track.addEventListener('scroll', onScroll, { passive: true });
    window.addEventListener('resize', updateArrows);
    return () => {
      track.removeEventListener('scroll', onScroll);
      window.removeEventListener('resize', updateArrows);
    };
  }, [updateArrows]);

  const scroll = (direction: 'left' | 'right') => {
    if (!trackRef.current) return;
    trackRef.current.scrollBy({
      left: direction === 'left' ? -trackRef.current.clientWidth * 0.8 : trackRef.current.clientWidth * 0.8,
      behavior: 'smooth',
    });
  };

  if (!Array.isArray(products) || products.length === 0) return null;

  return (
    <div className="my-4 rounded-lg border border-gray-100 bg-gray-50 p-4">
      <p className="mb-3 text-sm font-medium text-gray-600">Productos relacionados</p>
      <div className="relative">
        {/* Left arrow — hidden on mobile */}
        {canScrollLeft && (
          <button
            onClick={() => scroll('left')}
            className="absolute -left-2 top-1/2 z-10 hidden h-8 w-8 -translate-y-1/2 items-center justify-center rounded-full border border-gray-200 bg-white shadow-sm transition-colors hover:border-green-500 hover:text-green-600 sm:flex"
            aria-label="Anterior"
          >
            <i className="fa fa-chevron-left text-xs" />
          </button>
        )}

        <div
          ref={trackRef}
          className="flex gap-3 overflow-x-auto scroll-smooth [-ms-overflow-style:none] [scrollbar-width:none] [&::-webkit-scrollbar]:hidden"
          style={{ scrollSnapType: 'x mandatory' }}
        >
          {products.map(product => (
            <div key={product.id} className="flex w-[160px] flex-shrink-0 sm:w-[180px]" style={{ scrollSnapAlign: 'start' }}>
              <CompactProductCard product={product} onAddToCart={onAddToCart} />
            </div>
          ))}
        </div>

        {/* Right arrow — hidden on mobile */}
        {canScrollRight && (
          <button
            onClick={() => scroll('right')}
            className="absolute -right-2 top-1/2 z-10 hidden h-8 w-8 -translate-y-1/2 items-center justify-center rounded-full border border-gray-200 bg-white shadow-sm transition-colors hover:border-green-500 hover:text-green-600 sm:flex"
            aria-label="Siguiente"
          >
            <i className="fa fa-chevron-right text-xs" />
          </button>
        )}
      </div>
    </div>
  );
}
